/**
 * Sprint 10 Batch 2.1: Usage Limit Modal (Hard Stop)
 * Shows when user tries to exceed their plan limits
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  AlertOctagon, Users, FileText, UserPlus, 
  ArrowRight, Zap, Crown, X 
} from 'lucide-react';
import { formatPrice } from '../config/packages';
import { usePackageCatalog } from '../context/PackageCatalogContext';

const UsageLimitModal = ({ 
  isOpen,
  onClose,
  limitType, // 'leads' | 'salesReps' | 'offers'
  currentUsage,
  maxLimit,
  currentPlan,
  onUpgrade
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAvailablePackages, getPackageById } = usePackageCatalog();
  
  if (!isOpen) return null;
  
  const limitIcons = {
    leads: FileText,
    salesReps: UserPlus,
    offers: FileText,
  };
  
  const limitLabels = {
    leads: t('limits.leadsLabel', 'Leads'),
    salesReps: t('limits.salesRepsLabel', 'Included users'),
    offers: t('limits.offersLabel', 'Monthly Offers'),
  };
  
  const limitActions = {
    leads: t('limits.leadsAction', 'create more leads'),
    salesReps: t('limits.salesRepsAction', 'add more included users'),
    offers: t('limits.offersAction', 'send more offers'),
  };
  
  const Icon = limitIcons[limitType] || AlertOctagon;
  
  // Find recommended upgrade (next tier with higher limits)
  const getRecommendedUpgrade = () => {
    const available = getAvailablePackages();
    const currentPkg = getPackageById(currentPlan);
    const currentOrder = currentPkg?.sortOrder ?? 0;

    for (const pkg of available) {
      if ((pkg.sortOrder ?? 0) > currentOrder) {
        const newLimit = pkg.limits?.[limitType] ?? pkg.limits?.leads_per_month;
        if (newLimit === -1 || newLimit > maxLimit) {
          return pkg;
        }
      }
    }
    return available.find(p => p.id === 'STARTER') || available[0] || null;
  };
  
  const recommendedPkg = getRecommendedUpgrade();
  const newLimit = recommendedPkg?.limits[limitType];
  
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade(recommendedPkg.id);
    } else {
      navigate('/clinic/packages');
    }
    onClose?.();
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-400 rounded-xl flex items-center justify-center">
                <AlertOctagon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">
                  {t('limits.modalTitle', '{{type}} Limit Reached', { type: limitLabels[limitType] })}
                </h2>
                <p className="text-sm text-red-100">
                  {t('limits.modalSubtitle', 'Upgrade to {{action}}', { action: limitActions[limitType] })}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="close-limit-modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-5">
          {/* Current Status */}
          <div className="flex items-center justify-center p-4 bg-gray-50 rounded-xl">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Icon className="h-5 w-5 text-red-500" />
                <span className="text-2xl font-bold text-gray-900">{currentUsage}</span>
                <span className="text-gray-500">/</span>
                <span className="text-lg text-gray-600">{maxLimit}</span>
              </div>
              <p className="text-sm text-gray-600">
                {t('limits.currentUsage', '{{type}} used in your {{plan}} plan', { 
                  type: limitLabels[limitType], 
                  plan: getPackageById(currentPlan)?.displayName || currentPlan 
                })}
              </p>
            </div>
          </div>
          
          {/* Upgrade Option */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              {t('limits.upgradePrompt', 'Recommended upgrade:')}
            </p>
            
            <Card 
              className="border-2 border-green-200 bg-green-50 hover:border-green-400 transition-colors cursor-pointer"
              onClick={handleUpgrade}
              data-testid="recommended-upgrade-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      {recommendedPkg?.id === 'PRO' ? (
                        <Crown className="h-5 w-5 text-green-600" />
                      ) : (
                        <Zap className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {recommendedPkg?.displayName}
                      </h4>
                      <p className="text-sm text-green-700">
                        {newLimit === -1 
                          ? t('limits.unlimited', 'Unlimited {{type}}', { type: limitLabels[limitType].toLowerCase() })
                          : t('limits.newLimit', 'Up to {{count}} {{type}}', { count: newLimit, type: limitLabels[limitType].toLowerCase() })
                        }
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">
                      {formatPrice(recommendedPkg?.pricing)}
                    </div>
                    <ArrowRight className="h-4 w-4 text-green-500 ml-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={onClose}
            >
              {t('limits.maybeLater', 'Maybe Later')}
            </Button>
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={handleUpgrade}
              data-testid="upgrade-now-btn"
            >
              {t('limits.upgradeNow', 'Upgrade Now')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          
          {/* View All Plans Link */}
          <div className="text-center">
            <button 
              onClick={() => {
                navigate('/clinic/packages');
                onClose?.();
              }}
              className="text-sm text-blue-600 hover:underline"
              data-testid="view-all-plans-link"
            >
              {t('limits.viewAllPlans', 'View all plans and compare features')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageLimitModal;
